import React, { useState, useEffect } from 'react';
import { DropDownComponent, InputComponent, FileUploadComponent } from '../custcomponents';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { AxiosAdminInstance } from '../utils';

interface Sponsor {
    id: string;
    name: string;
    website: string;
    category: string;
    imageurl: string;
    priority: number;
}

export const ManageSponsors = () => {
    const { eventcode } = useParams<{ eventcode: string }>();
    const [sponsors, setSponsors] = useState<Sponsor[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingSponsor, setEditingSponsor] = useState<Sponsor | null>(null);
    const [newSponsor, setNewSponsor] = useState({
        name: '',
        website: '',
        category: '',
        priority: 0,
        imageFile: null as File | null,
        imageurl: '',
        contentType: ''
    });



    useEffect(() => {
        fetchSponsors();
    }, []);

    useEffect(() => {
        if (editingSponsor) {
            setNewSponsor({
                name: editingSponsor.name,
                website: editingSponsor.website,
                category: editingSponsor.category,
                priority: editingSponsor.priority,
                imageFile: null,
                imageurl: editingSponsor.imageurl,
                contentType: ''
            });
        } else {
            setNewSponsor({
                name: '',
                website: '',
                category: '',
                priority: 0,
                imageFile: null,
                imageurl: '',
                contentType: ''
            });
        }
    }, [editingSponsor]);

    const fetchSponsors = async () => {
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageSponsors', {
                action: 'getSponsors',
                data: { eventcode }
            });
            if (response.data.success) {
                const sponsorsObj = response.data.sponsors || {};
                const sponsorsArray = Object.keys(sponsorsObj).map(id => ({
                    id,
                    ...sponsorsObj[id]
                }));
                sponsorsArray.sort((a, b) => b.priority - a.priority);
                setSponsors(sponsorsArray);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to fetch sponsors');
        }
        setLoading(false);
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64 = reader.result as string;
                // Remove the data:image/jpeg;base64, prefix
                const base64Data = base64.split(',')[1];
                resolve(base64Data);
            };
            reader.onerror = error => reject(error);
        });
    };

    const handleAddSponsor = async () => {
        {/* if (!newSponsor.name.trim()) {
            toast.error('Sponsor name is required');
            return;
        }*/}
        if (!newSponsor.category) {
            toast.error('Category is required');
            return;
        }
        setLoading(true);
        try {
            let data = {
                eventcode,
                name: newSponsor.name,
                website: newSponsor.website,
                category: newSponsor.category,
                priority: newSponsor.priority,
                imageurl: newSponsor.imageurl
            };
            if (editingSponsor) {
                data = { ...data, sponsorId: editingSponsor.id } as any;
            }
            if (newSponsor.imageFile) {
                const imageBase64 = await fileToBase64(newSponsor.imageFile);
                data = { ...data, imageBase64, imageFileName: newSponsor.imageFile.name, contentType: newSponsor.contentType } as any;
            }
            const response = await AxiosAdminInstance.post('/api/admin/manageSponsors', {
                action: editingSponsor ? 'editSponsor' : 'addSponsor',
                data
            });
            if (response.data.success) {
                toast.success(editingSponsor ? 'Sponsor updated successfully' : 'Sponsor added successfully');
                setNewSponsor({
                    name: '',
                    website: '',
                    category: '',
                    priority: 0,
                    imageFile: null,
                    imageurl: '',
                    contentType: ''
                });
                setEditingSponsor(null);
                fetchSponsors();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(editingSponsor ? 'Failed to update sponsor' : 'Failed to add sponsor');
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setNewSponsor({
            name: '',
            website: '',
            category: '',
            priority: 0,
            imageFile: null,
            imageurl: '',
            contentType: ''
        });
        setEditingSponsor(null);
    };

    const handleDeleteSponsor = async (sponsorId: string) => {
        if (!window.confirm('Are you sure you want to delete this sponsor?')) return;
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageSponsors', {
                action: 'deleteSponsor',
                data: { eventcode, sponsorId }
            });
            if (response.data.success) {
                toast.success('Sponsor deleted successfully');
                fetchSponsors();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to delete sponsor');
        }
        setLoading(false);
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<boolean> => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return false;
            }
            // Validate file size (100KB max)
            if (file.size > 100 * 1024) {
                toast.error('File size must be less than 100KB');
                return false;
            }
            setNewSponsor(prev => ({ ...prev, imageFile: file, imageurl: '', contentType: file.type }));
            return true;
        }
        return false;
    };

    const sponsorColumns = [
        { name: 'Actions', cell: (row: Sponsor) => (
            <div>
                <button className="btn btn-sm btn-warning me-1" onClick={() => setEditingSponsor(row)} title="Edit">
                    <i className="fa fa-edit"></i>
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDeleteSponsor(row.id)} title="Delete">
                    <i className="fa fa-trash"></i>
                </button>
            </div>
        ) },
        { name: 'Name', selector: (row: Sponsor) => row.name, sortable: true },
        { name: 'Website', selector: (row: Sponsor) => row.website, sortable: true },
        { name: 'Category', selector: (row: Sponsor) => row.category, sortable: true },
        { name: 'Priority', selector: (row: Sponsor) => row.priority, sortable: true },
        { name: 'Image', cell: (row: Sponsor) => (
            row.imageurl ? <img src={row.imageurl} alt={row.name || "Sponsor Logo"} style={{ width: '50px', height: '50px', objectFit: 'cover' }} /> : 'No Image'
        ) }
    ];

    return (
        <>
            <section className="register-sec sec-padding-admin" data-scroll-index="8">
                <div className="container">
                    <div className="sec-title">
                        <span className="icon-inner wow flipInY" data-wow-duration="300ms">
                            <span className="fa-stack">
                                <i className="fa rhex fa-stack-2x"></i>
                                <i className="fa fa-money fa-stack-1x"></i>
                            </span>
                        </span>
                        <h3 className="wow fadeInRight" data-wow-duration="500ms">Manage Sponsors</h3>
                    </div>
                    <div className="register-sec-details">
                        <form onSubmit={(e) => { e.preventDefault(); handleAddSponsor(); }}>
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">{editingSponsor ? 'Edit Sponsor' : 'Add New Sponsor'}</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="name"
                                            custplaceholder="Sponsor Name"
                                            custid="sponsorName"
                                            onExternalValue={(val: string) => setNewSponsor(prev => ({ ...prev, name: val }))}
                                            onExternalBind={newSponsor.name}
                                        />
                                    </div>
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="website"
                                            custplaceholder="Website"
                                            custid="sponsorWebsite"
                                            onExternalValue={(val: string) => setNewSponsor(prev => ({ ...prev, website: val }))}
                                            onExternalBind={newSponsor.website}
                                        />
                                    </div>
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="category"
                                            custplaceholder="Category"
                                            custid="sponsorCategory"
                                            onExternalValue={(val: string) => setNewSponsor(prev => ({ ...prev, category: val }))}
                                            onExternalBind={newSponsor.category}
                                        />
                                    </div>
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="priority"
                                            custplaceholder="Priority (number)"
                                            custid="sponsorPriority"
                                            onExternalValue={(val: string) => setNewSponsor(prev => ({ ...prev, priority: parseInt(val) || 0 }))}
                                            onExternalBind={newSponsor.priority?.toString()}
                                        />
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <FileUploadComponent
                                            custname="image"
                                            custplaceholder="Upload Image"
                                            custid="sponsorImage"
                                            onChange={handleFileChange}
                                        />
                                        {newSponsor.imageurl && (
                                            <div className="mt-2">
                                                <img src={newSponsor.imageurl} alt="Current" style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        <button className="btn btn-primary" type="submit" disabled={loading}>
                                            {editingSponsor ? 'Update Sponsor' : 'Add Sponsor'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                        </button>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                      
                                        {editingSponsor && (
                                            <button className="btn btn-secondary ms-2" type="button" onClick={handleCancel}>
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Sponsors</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <DataTable
                                            columns={sponsorColumns}
                                            data={sponsors}
                                            customStyles={{
                                                cells: {
                                                    style: {
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'normal',
                                                        padding: '8px'
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};
